'use client';

import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import Button from '../Button/Button';
import { ForwardedButtonRef } from '../Button/Button.types';
import { DownloadButtonProps } from './DownloadButton.types';

/**
 * A CTA Button that allows the user to download the appropriate version of the app based on their OS
 * Automatically detects OS and architecture to show the most relevant download option(s)
 *
 * Performance optimizations:
 * - Memoized button components to prevent unnecessary re-renders
 * - Extracted user agent detection logic to avoid duplication
 * - Optimized dependency arrays to prevent unnecessary effect runs
 */
const DownloadButton = (
  props: DownloadButtonProps,
  ref: ForwardedRef<ForwardedButtonRef>,
) => {
  const {
    children,
    customTrackingEvent,
    windowsLink,
    macArmLink,
    macIntelLink,
    macLabel,
    windowsLabel,
  } = props;
  const [os, setOs] = useState<'macos' | 'windows' | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [architectureDetected, setArchitectureDetected] =
    useState<boolean>(false);

  // Memoize tracking event to prevent unnecessary re-renders
  const trackingEvent = useMemo(
    () => [customTrackingEvent],
    [customTrackingEvent],
  );

  // Extract user agent detection logic
  const detectOSFromUserAgent = useCallback(() => {
    const userAgent = navigator.userAgent.toLowerCase();

    if (
      userAgent.includes('mac') &&
      !userAgent.includes('iphone') &&
      !userAgent.includes('ipad')
    ) {
      setOs('macos');
      setArchitectureDetected(false);
    } else if (userAgent.includes('win') && !userAgent.includes('phone')) {
      setOs('windows');
      setArchitectureDetected(true);
      setLink(windowsLink);
    }
    // If it's not clearly Windows or macOS (mobile, Linux, etc.), don't set OS
  }, [windowsLink]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).navigator?.userAgentData) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).navigator.userAgentData
        .getHighEntropyValues(['architecture'])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((values: any) => {
          const architecture = values?.architecture.toLowerCase();
          const platform = values?.platform?.toLowerCase();
          if (platform === 'macos') {
            setOs('macos');
            setArchitectureDetected(true);

            if (architecture === 'arm') {
              setLink(macArmLink);
            } else {
              setLink(macIntelLink);
            }
          }
          if (platform === 'windows') {
            setOs('windows');
            setArchitectureDetected(true);
            setLink(windowsLink);
          }
        })
        .catch(detectOSFromUserAgent);
    } else {
      detectOSFromUserAgent();
    }
  }, [windowsLink, macArmLink, macIntelLink, detectOSFromUserAgent]);

  const $button = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      $button,
    }),
    [],
  );

  // Memoize button components to prevent unnecessary re-renders
  const windowsButton = useMemo(
    () => (
      <Button
        {...props}
        to={{ url: link || windowsLink, type: 'external' }}
        ref={$button}
        customTrackingEvent={trackingEvent}
      >
        {children || windowsLabel}
      </Button>
    ),
    [props, link, windowsLink, trackingEvent, children, windowsLabel],
  );

  const macArmButton = useMemo(
    () => (
      <Button
        {...props}
        to={{ url: macArmLink, type: 'external' }}
        ref={$button}
        customTrackingEvent={trackingEvent}
      >
        {children || `${macLabel} (Apple Silicon)`}
      </Button>
    ),
    [props, macArmLink, trackingEvent, children, macLabel],
  );

  const macIntelButton = useMemo(
    () => (
      <Button
        {...props}
        to={{ url: macIntelLink, type: 'external' }}
        ref={$button}
        customTrackingEvent={trackingEvent}
      >
        {children || `${macLabel} (Intel)`}
      </Button>
    ),
    [props, macIntelLink, trackingEvent, children, macLabel],
  );

  // Memoize specific Mac button for detected architecture
  const detectedMacButton = useMemo(() => {
    if (os !== 'macos' || !architectureDetected || !link) return null;

    return (
      <Button
        {...props}
        to={{ url: link, type: 'external' }}
        ref={$button}
        customTrackingEvent={trackingEvent}
      >
        {children || macLabel}
      </Button>
    );
  }, [
    os,
    architectureDetected,
    link,
    props,
    trackingEvent,
    children,
    macLabel,
  ]);

  // Render logic based on detection state
  if (os === 'macos' && architectureDetected) {
    return detectedMacButton;
  }

  if (os === 'windows') {
    return windowsButton;
  }

  if (os === 'macos' && !architectureDetected) {
    return (
      <>
        {macArmButton} {macIntelButton}
      </>
    );
  }

  // Default: show all 3 buttons
  return (
    <>
      {macArmButton} {macIntelButton} {windowsButton}
    </>
  );
};

export default forwardRef(DownloadButton);
